import { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { IntercomProvider } from 'react-use-intercom';
import TagManager from 'react-gtm-module';
import * as Sentry from '@sentry/react';

import { useAuth } from './hooks/useAuth';

import NoMatch from './routes/NoMatch';
import SignIn from './routes/SignIn';
import SignUp from './routes/SignUp';
import VerifySignUp from './routes/VerifySignUp';
import VerifyForgotPassword from './routes/VerifyForgotPassword';
import DashboardRoutes from './routes/DashboardRoutes';
import ForgotPassword from './routes/ForgotPassword';
import UserProfile from './routes/UserProfile';
import AdminRoutes from './routes/AdminRoutes';
import Orders from './routes/mywork/Orders';
import SavedAudiences from './routes/mywork/SavedAudiences';
import UploadedFiles from './routes/mywork/UploadedFiles';
import AskAddyChats from './routes/mywork/AskAddyChats';
import DashboardIndex from './routes/DashboardIndex';
import MyWorkRoutes from './routes/mywork/MyWorkRoutes';
import EnhancementsFileSelection from './routes/enhancements/EnhancementsFileSelection';
import InsightsFileSelection from './routes/insights/InsightsFileSelection';
import InsightRoutesWithFileContext from './routes/insights/InsightRoutesWithFileContext';
import EnhancementRoutesWithFileContext from './routes/enhancements/EnhancementRoutesWithFileContext';

import AllComponents from './components/AllComponents';
import LoadingOverlay from './components/LoadingOverlay';
import GlobalErrorFallback from './components/errors/GlobalErrorFallback';
import Packages from './routes/enhancements/Packages';
import PurchasePrompt from './routes/insights/PurchasePrompt';
import { PurchasePageProvider } from './hooks/usePurchasePage';
import OrderAudience from './routes/audiences/OrderAudience';
import AudienceOrderSummary from './routes/audiences/AudienceOrderSummary';
import ActivationBuilder from './routes/activations/ActivationBuilder';
import DestinationConnect from './routes/activations/DestinationConnect';
import EnhancementOrderSummary from './routes/enhancements/EnhancementOrderSummary';
import InsightOrderSummary from './routes/insights/InsightOrderSummary';
import AgreeToTerms from './routes/AgreeToTerms';
import PurchaseSubscription from './routes/profile/PurchaseSubscription';
import { OrderProvider } from './hooks/useOrder';
import OrderEnhancement from './routes/enhancements/OrderEnhancement';
import OrderAudienceEnhancement from './routes/enhancements/OrderAudienceEnhancement';
import AudiencePackages from './routes/enhancements/AudiencePackages';
import OrderInsight from './routes/insights/OrderInsight';
import ExistingOrderRedirect from './components/payments/ExistingOrderRedirect';
import AudiencePurchasePrompt from './routes/insights/AudiencePurchasePrompt';
import OrderAudienceProfile from './routes/insights/OrderAudienceProfile';
import AdminUsers from './routes/admin/AdminUsers';
import AdminMaintenance from './routes/admin/AdminMaintenance';
import AdminOrders from './routes/admin/AdminOrders';
import RatecardsPage from './routes/admin/RatecardsPage';
import AskAddyUsagePage from './routes/admin/AskAddyUsagePage';
import AskAddy from './routes/askAddy/AskAddy';

import { ActionModalProvider } from './hooks/useActionModal';
import AudienceBuilderWrapper from './routes/audiences/AudienceBuilderWrapper';
import AudienceUpsell from './routes/audiences/AudienceUpsell/index';
import MaintenancePage from './components/MaintenancePage';
import Intercom from './components/Intercom';
import CustomPackageBuilderWrapper from './routes/enhancements/CustomPackageBuilderWrapper';
import Distributions from './routes/mywork/Distributions';
import DistributionSummary from './routes/activations/DistributionSummary';
import { ActivationProvider } from './routes/activations/ActivationContext';

const SHOW_INTERCOM = import.meta.env.VITE_SHOW_INTERCOM;
const ENV = import.meta.env.VITE_ENV;
const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID;
const GTM_ID = import.meta.env.VITE_GTM_ID;
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

TagManager.initialize({
  gtmId: GTM_ID,
  dataLayer: {
    env: ENV,
  },
});

function NavigateBasedOnUser() {
  const { user, attemptingSilentRefresh } = useAuth();

  if (attemptingSilentRefresh) {
    return <LoadingOverlay />;
  }

  return user ? <Navigate to="/dashboard" /> : <Navigate to="/signin" />;
}

const RoutesWrapper = SENTRY_DSN
  ? Sentry.withSentryReactRouterV6Routing(Routes)
  : Routes;

function App() {
  const { user } = useAuth();

  useEffect(() => {
    if (user?.id) {
      window.dataLayer.push({
        userId: user.id,
      });
    }
  }, [user]);

  return (
    <ErrorBoundary FallbackComponent={GlobalErrorFallback}>
      <MaintenancePage />
      <IntercomProvider appId={INTERCOM_APP_ID}>
        {SHOW_INTERCOM && <Intercom />}
        <RoutesWrapper>
          <Route path="/" element={<NavigateBasedOnUser />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/terms" element={<AgreeToTerms />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route
            path="/verifysignup/:verificationToken"
            element={<VerifySignUp />}
          />
          <Route
            path="/verifyforgotpassword/:resetToken"
            element={<VerifyForgotPassword />}
          />

          <Route
            path="/admin"
            element={
              <ActionModalProvider>
                <AdminRoutes />
              </ActionModalProvider>
            }
          >
            <Route index element={<AdminUsers />} />
            <Route path="maintenance" element={<AdminMaintenance />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="ratecards" element={<RatecardsPage />} />
            <Route path="ask-addy-usage" element={<AskAddyUsagePage />} />
          </Route>

          <Route path="/dashboard" element={<DashboardRoutes />}>
            <Route index element={<DashboardIndex />} />
            <Route path="audiences" element={<Outlet />}>
              <Route index element={<Navigate to="audiencebuilder" />} />
              <Route
                path="audiencebuilder"
                element={<AudienceBuilderWrapper />}
              />
              <Route
                path="order"
                element={
                  <OrderProvider>
                    <Outlet />
                  </OrderProvider>
                }
              >
                <Route index element={<OrderAudience />} />
                <Route path="upsell" element={<AudienceUpsell />} />
                <Route path=":orderId" element={<AudienceOrderSummary />} />
                <Route
                  path="abandoned/:orderId"
                  element={
                    <ExistingOrderRedirect
                      createStateFn={(order) => ({
                        orderDetails: {
                          orderId: order.id,
                          coreProductName: 'audience',
                          audienceName: order.audienceName,
                          recordsIncluded: order.recordsIncluded,
                        },
                      })}
                    />
                  }
                />
              </Route>
            </Route>
            <Route
              path="activations"
              element={
                <ActivationProvider>
                  <Outlet />
                </ActivationProvider>
              }
            >
              <Route index element={<Navigate to="activationbuilder" />} />
              <Route path="activationbuilder" element={<Outlet />}>
                <Route index element={<ActivationBuilder />} />
                {/* The Configure and Confirmation steps used to be standalone
                    routes; they're now internal steps of the wizard. Redirect
                    legacy URLs back to the wizard entry. */}
                <Route
                  path="configureactivation"
                  element={<Navigate to=".." replace />}
                />
                <Route
                  path="confirmation"
                  element={<Navigate to=".." replace />}
                />
                <Route
                  path="connectdestination/:slug"
                  element={<DestinationConnect />}
                />
              </Route>
              <Route
                path="summary/:activationId"
                element={<DistributionSummary />}
              />
            </Route>
            <Route path="enhancements" element={<Outlet />}>
              <Route index element={<Navigate to="packages" />} />
              <Route path="packages" element={<Packages />} />
              <Route
                path="custom"
                element={
                  <OrderProvider>
                    <CustomPackageBuilderWrapper />
                  </OrderProvider>
                }
              />
              <Route
                path="fileselection"
                element={<EnhancementsFileSelection />}
              />
              <Route
                path=":fileId"
                element={<EnhancementRoutesWithFileContext />}
              >
                <Route index element={<Navigate to="packages" />} />
                <Route path="packages" element={<Packages />} />
                <Route
                  path="custom"
                  element={
                    <OrderProvider>
                      <CustomPackageBuilderWrapper />
                    </OrderProvider>
                  }
                />
              </Route>
              <Route
                path="order"
                element={
                  <OrderProvider>
                    <Outlet />
                  </OrderProvider>
                }
              >
                <Route index element={<OrderEnhancement />} />
                <Route path="audience" element={<AudiencePackages />} />
                <Route path="audience/custom" element={<CustomPackageBuilderWrapper />} />
                <Route path="audience/:orderId" element={<OrderAudienceEnhancement />} />
                <Route path=":orderId" element={<EnhancementOrderSummary />} />
              </Route>
            </Route>
            {/* Ask Addy — top-level: pick a report (dropdown) then AI chat */}
            <Route path="ask-addy" element={<AskAddy />} />
            <Route path="ask-addy/:packageId" element={<AskAddy />} />
            <Route path="insights" element={<Outlet />}>
              <Route index element={<Navigate to="fileselection" />} />
              <Route path="fileselection" element={<InsightsFileSelection />} />

              {/* Seed file routes */}
              <Route path=":fileId" element={<InsightRoutesWithFileContext />}>
                <Route index element={<PurchasePrompt />} />
              </Route>
              <Route
                path="order"
                element={
                  <OrderProvider>
                    <Outlet />
                  </OrderProvider>
                }
              >
                <Route index element={<OrderInsight />} />
                <Route path=":orderId" element={<InsightOrderSummary />} />
                <Route
                  path="abandoned/:orderId"
                  element={
                    <ExistingOrderRedirect
                      createStateFn={(order) => ({
                        orderDetails: {
                          orderId: order.id,
                          coreProductName: 'profileReport',
                          insightFileName: order.insightFileName,
                        },
                      })}
                    />
                  }
                />
              </Route>

              {/* Audience routes - NEW */}
              <Route path="audience/:audienceId" element={<AudiencePurchasePrompt />} />
              <Route
                path="audience/order"
                element={
                  <OrderProvider>
                    <Outlet />
                  </OrderProvider>
                }
              >
                <Route index element={<OrderAudienceProfile />} />
                <Route path=":orderId" element={<InsightOrderSummary />} />
              </Route>
            </Route>
            <Route path="mywork" element={<MyWorkRoutes />}>
              <Route index element={<Navigate to="audiences" />} />
              <Route path="distributions" element={<Distributions />} />{' '}
              <Route path="orders" element={<Orders />} />
              <Route path="audiences" element={<SavedAudiences />} />
              <Route path="files" element={<UploadedFiles />} />
              <Route path="ask-addy" element={<AskAddyChats />} />
              <Route path="*" element={<Navigate to="audiences" />} />
            </Route>
            <Route path="profile" element={<Outlet />}>
              <Route index element={<UserProfile />} />
              <Route
                path="subscription"
                element={
                  <PurchasePageProvider>
                    <Outlet />
                  </PurchasePageProvider>
                }
              >
                <Route path="order" element={<PurchaseSubscription />} />
              </Route>
            </Route>
          </Route>
          {ENV && ['local', 'dev'].includes(ENV) && (
            <Route path="/components" element={<AllComponents />} />
          )}
          <Route path="*" element={<NoMatch />} />
        </RoutesWrapper>
      </IntercomProvider>
    </ErrorBoundary>
  );
}

export default App;
